import { useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import PageHeader from "../-components/PageHeader";
import Seo from "components/Seo";
import { toastErrorSyle } from "hooks/utils/util";
import {
    HolidayBody,
    useDeleteConsoleHoliday,
    useGetConsoleHolidays,
    useRestoreConsoleHoliday,
    usePostConsoleHoliday,
    usePutConsoleHoliday,
    usePutConsoleHolidayConfig,
    useSyncConsoleHoliday,
} from "hooks/console/useConsoleHoliday";
import HolidayToolbar from "./holiday/HolidayToolbar";
import HolidayCalendar from "./holiday/HolidayCalendar";
import HolidayMonthList from "./holiday/HolidayMonthList";
import HolidayEditDialog from "./holiday/HolidayEditDialog";
import { isWeekendStr, todayKst } from "./holiday/holidayUtils";

// 모닝택배 > 고객서비스 > 휴무일 관리 (캘린더)
export default function ConsoleMorningHolidays() {
    const today = todayKst();
    const thisYear = Number(today.slice(0, 4));
    const [month, setMonth] = useState(() => new Date(thisYear, Number(today.slice(5, 7)) - 1, 1));
    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    const year = month.getFullYear();
    const monthNo = month.getMonth() + 1;
    const { data, isLoading } = useGetConsoleHolidays(year, monthNo);
    const { mutate: postHoliday, isPending: isPosting } = usePostConsoleHoliday();
    const { mutate: putHoliday, isPending: isPutting } = usePutConsoleHoliday();
    const { mutate: deleteHoliday } = useDeleteConsoleHoliday();
    const { mutate: restoreHoliday, isPending: isRestoring } = useRestoreConsoleHoliday();
    const { mutate: syncHoliday, isPending: isSyncing } = useSyncConsoleHoliday();
    const { mutate: putConfig, isPending: isSavingConfig } = usePutConsoleHolidayConfig();

    const holidays = useMemo(() => data?.data_list ?? [], [data]);
    const holidayMap = useMemo(() => new Map(holidays.map(h => [h.holiday_date, h])), [holidays]);
    const selectedHoliday = selectedDate ? holidayMap.get(selectedDate) : undefined;
    // 삭제된 공휴일(툼스톤) — 셀에 취소선 표시, 모달에서 "공휴일로 복원"
    const deletedMap = useMemo(() => new Map((data?.deleted_holidays ?? []).map(h => [h.holiday_date, h])), [data]);
    const selectedDeleted = selectedDate && !selectedHoliday ? deletedMap.get(selectedDate) : undefined;
    // 동기화 허용 범위(BE·PM 공휴일 API 와 동일): 올해 ~ 내년
    const syncYearOptions = [thisYear, thisYear + 1];

    const handleDayClick = (dateStr: string) => {
        const holiday = holidayMap.get(dateStr);
        // 삭제된 공휴일은 주말이어도 복원할 수 있게 모달을 연다(지난 날짜 제외)
        if (!holiday && deletedMap.has(dateStr) && dateStr >= today) {
            setSelectedDate(dateStr);
            return;
        }
        if (!holiday && isWeekendStr(dateStr)) {
            toast("주말은 자동 휴무입니다.");
            return;
        }
        if (!holiday && dateStr < today) {
            toast("지난 날짜는 등록할 수 없습니다.");
            return;
        }
        setSelectedDate(dateStr);
    };

    const closeDialog = () => setSelectedDate(null);
    const onError = (error: Error) => toast.error(error.message, toastErrorSyle);

    const onSaved = (message: string) => () => {
        toast.success(message);
        closeDialog();
    };

    const handleSubmit = (body: HolidayBody) => {
        if (!selectedDate) return;
        if (selectedHoliday) {
            putHoliday(
                { holiday_no: selectedHoliday.holiday_no, ...body },
                { onSuccess: onSaved("휴무일이 수정되었습니다."), onError },
            );
        } else {
            postHoliday(
                { holiday_date: selectedDate, ...body },
                { onSuccess: onSaved("휴무일이 등록되었습니다."), onError },
            );
        }
    };

    const handleDelete = (closeConfirm: () => void) => {
        if (!selectedHoliday) return;
        deleteHoliday(
            { holiday_no: selectedHoliday.holiday_no },
            {
                onSuccess: () => {
                    toast.success("휴무일이 삭제되었습니다.");
                    closeConfirm();
                    closeDialog();
                },
                onError,
            },
        );
    };

    const handleRestore = () => {
        if (!selectedDeleted) return;
        restoreHoliday({ holiday_no: selectedDeleted.holiday_no }, { onSuccess: onSaved("공휴일이 복원되었습니다."), onError });
    };

    const handleSync = (targetYear: number) => {
        syncHoliday(
            { year: targetYear },
            {
                onSuccess: result =>
                    toast.success(`${result.year}년 공휴일 ${result.fetched}건 확인 · ${result.inserted}건 추가`),
                onError,
            },
        );
    };

    const handleSaveConfig = (value: string) => {
        putConfig({ default_end_time: value }, { onSuccess: () => toast.success("기본 업무 종료시간이 저장되었습니다."), onError });
    };

    return (
        <>
            <Seo type="console" pageTitle="휴무일 관리" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="고객서비스" title="휴무일 관리" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    {/* 콘솔 셸(고정 사이드바)이 데스크톱 전용이라 형제 화면처럼 최소 폭 + 가로 스크롤로 대응 */}
                    <div className="px-[40px] py-[28px] w-full overflow-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[760px]">
                            <HolidayToolbar
                                month={month}
                                onMonthChange={setMonth}
                                defaultEndTime={data?.default_end_time ?? "18:00"}
                                onSaveDefaultEndTime={handleSaveConfig}
                                isSavingConfig={isSavingConfig}
                                syncYearOptions={syncYearOptions}
                                onSync={handleSync}
                                isSyncing={isSyncing}
                            />

                            {/* 0건 안내 — 해당 연도 공휴일을 아직 불러오지 않음 */}
                            {data && data.public_holiday_count === 0 && syncYearOptions.includes(year) && (
                                <div className="mt-[16px] flex flex-wrap items-center gap-[8px] rounded-[8px] bg-[#EEF2FF] px-[16px] py-[12px] text-[14px] text-[#3730A3]">
                                    {year}년 공휴일을 아직 불러오지 않았습니다.
                                    <button type="button" disabled={isSyncing} onClick={() => handleSync(year)} className="underline font-[600] disabled:opacity-50">
                                        공휴일 불러오기
                                    </button>
                                </div>
                            )}

                            <div className="mt-[16px] relative">
                                {isLoading && <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center text-[#666666]">불러오는 중…</div>}
                                <HolidayCalendar
                                    month={month}
                                    holidayMap={holidayMap}
                                    deletedMap={deletedMap}
                                    loading={isLoading}
                                    today={today}
                                    onDayClick={handleDayClick}
                                />
                            </div>

                            <HolidayMonthList holidays={holidays} onSelect={handleDayClick} />
                        </div>
                    </div>
                </div>
            </div>

            <HolidayEditDialog
                open={!!selectedDate}
                onOpenChange={open => !open && closeDialog()}
                dateStr={selectedDate ?? ""}
                holiday={selectedHoliday}
                readOnly={!!selectedDate && selectedDate < today}
                isSubmitting={isPosting || isPutting}
                onSubmit={handleSubmit}
                onDelete={handleDelete}
                deletedHoliday={selectedDeleted}
                onRestore={handleRestore}
                isRestoring={isRestoring}
            />
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
